import React, { useCallback, useRef } from 'react';
import { Node, useReactFlow } from 'reactflow';
import { clearMultiSelection } from '@components/MainStage/clearMultiSelection';
import { getMultiSelectionState } from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import useAssets from '@hooks/useAssets';
import useProjectId from '@hooks/useProjectId';
import { useUpdateNodePositionMutation } from '@store/services/nodes';
import {
  clampGroupDelta,
  getVisibleSelectedNodes,
} from '@utils/canvas/groupDrag';
import NodeView from '@utils/canvas/NodeView';
import { isElementLocal } from '@utils/drawers';

interface GroupDragState {
  ids: string[];
  startPositions: Map<string, { x: number; y: number }>;
  startPointer: { x: number; y: number };
}

const useDragNode = ({
  menu,
  closeMenu,
}: {
  menu: any;
  closeMenu: () => void;
}) => {
  const nodeDragRef = useRef<Node | null>(null);
  const groupDragRef = useRef<GroupDragState | null>(null);
  const [updateNodePositionRequest] = useUpdateNodePositionMutation();
  const { selectedAssetId } = useAssets();
  const projectId = useProjectId(true);
  const reactFlow = useReactFlow();

  const {
    updateNodePosition,
    updateNodePositions,
    updateNode,
    unselectActiveEdge,
    unselectActiveNode,
  } = useStore();

  const persistPositions = useCallback(
    (updatedNodes: { id: string; x: number; y: number }[]) => {
      const requestParams = {
        projectId,
        ...(selectedAssetId && { asset_id: selectedAssetId }),
        nodes: updatedNodes,
      };

      updateNodePositionRequest(requestParams).unwrap();
    },
    [projectId, selectedAssetId],
  );

  const onNodeDragStart = useCallback(
    (event: React.MouseEvent, node: Node) => {
      const activeEdge = useStore.getState().getActiveEdge();
      if (!activeEdge || !isElementLocal(activeEdge.id)) unselectActiveEdge();

      unselectActiveNode();
      if (menu) closeMenu();
      nodeDragRef.current = node;

      const allNodes = useStore.getState().nodes;
      const { selectedIds } = getMultiSelectionState();
      const groupNodes = getVisibleSelectedNodes(selectedIds, allNodes);
      const isGroupDrag =
        groupNodes.length >= 2 &&
        groupNodes.some((groupNode) => groupNode.id === node.id);

      if (isGroupDrag) {
        const startPositions = new Map(
          groupNodes.map((groupNode) => [
            groupNode.id,
            { x: groupNode.position.x, y: groupNode.position.y },
          ]),
        );

        groupDragRef.current = {
          ids: groupNodes.map((groupNode) => groupNode.id),
          startPositions,
          startPointer: { x: event.clientX, y: event.clientY },
        };

        groupNodes.forEach((groupNode) =>
          updateNode(groupNode.id, { extent: undefined }),
        );
      } else {
        groupDragRef.current = null;
        clearMultiSelection();
      }
    },
    [menu],
  );

  const onNodeDrag = useCallback(
    (event: React.MouseEvent, node: Node) => {
      const groupDrag = groupDragRef.current;
      if (!groupDrag) return;

      if (!groupDrag.startPositions.has(node.id)) return;

      const allNodes = useStore.getState().nodes;
      const groupNodes = allNodes.filter((candidate) =>
        groupDrag.ids.includes(candidate.id),
      );

      const zoom = reactFlow.getZoom() || 1;
      const raw = {
        dx: (event.clientX - groupDrag.startPointer.x) / zoom,
        dy: (event.clientY - groupDrag.startPointer.y) / zoom,
      };

      const { dx, dy } = clampGroupDelta(
        groupNodes,
        groupDrag.startPositions,
        allNodes,
        raw,
      );

      updateNodePositions(
        groupDrag.ids.map((id) => {
          const memberStart = groupDrag.startPositions.get(id)!;

          return { id, x: memberStart.x + dx, y: memberStart.y + dy };
        }),
      );
    },
    [reactFlow],
  );

  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const groupDrag = groupDragRef.current;

      if (node.type === NodesTypes.NewNode) {
        groupDragRef.current = null;
        nodeDragRef.current = null;
        return;
      }

      if (groupDrag) {
        const allNodes = useStore.getState().nodes;

        const updatedNodes = groupDrag.ids
          .map((id) => {
            const memberNode = allNodes.find(
              (candidate) => candidate.id === id,
            );
            if (!memberNode) return null;

            const snappedY = new NodeView(id, memberNode).YPosition;

            return { id, x: memberNode.position.x, y: snappedY };
          })
          .filter(
            (updated): updated is { id: string; x: number; y: number } =>
              updated !== null,
          );

        updateNodePositions(updatedNodes.map(({ id, y }) => ({ id, y })));

        groupDrag.ids.forEach((id) => updateNode(id, { extent: 'parent' }));

        persistPositions(updatedNodes);

        groupDragRef.current = null;
        nodeDragRef.current = null;
        return;
      }

      const nodeView = new NodeView(node.id);

      if (nodeDragRef.current) {
        updateNodePosition(nodeDragRef.current.id, { y: nodeView.YPosition });
        persistPositions([
          { id: node.id, x: node.position.x, y: nodeView.YPosition },
        ]);
      }

      nodeDragRef.current = null;
    },
    [persistPositions],
  );

  return { onNodeDragStart, onNodeDrag, onNodeDragStop };
};

export default useDragNode;
